import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import { ArrowUpRight } from "lucide-react";
import { WHATSAPP_LINK } from "@/lib/api";
import { PORTRAITS, PORTRAIT_ALT } from "@/data/portraits";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { breadcrumbListJsonLd, medicalWebPageJsonLd, howToJsonLd } from "@/data/schema";

const STEPS = [
  { n: "01", t: "Reach out", d: "WhatsApp the practice number or submit a consultation request. If your case is urgent, use the Urgent Evaluation path.", meta: "WhatsApp · Form · Urgent path" },
  { n: "02", t: "Team pre-screening", d: "A clinical coordinator (not a call centre) collects your existing imaging, reports, and history. Nothing you send here is asked for again.", meta: "Within one working hour" },
  { n: "03", t: "Video consultation with Dr Sidhu", d: "Dr Sidhu personally reviews your imaging with you and, if you wish, your family. You leave with an understood plan — not a procedure sold.", meta: "45–60 minutes · Recorded on request" },
  { n: "04", t: "Scheduling & pre-arrival coordination", d: "If a procedure is agreed, admission at Max Mohali is coordinated. For NRIs, arrival logistics and pre-arrival investigations are aligned.", meta: "Fully coordinated" },
  { n: "05", t: "Procedure & recovery", d: "Performed by Dr Sidhu, in the Max IR suite. Same-day discharge for most cases. In-person review before you leave.", meta: "Max Mohali · Vascular IR suite" },
  { n: "06", t: "Follow-up, on the same thread", d: "Post-discharge check-ins on the same WhatsApp thread you started on — including after you've returned home, wherever home is.", meta: "6-week, 6-month, on-demand" },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="How it works — from first WhatsApp to final follow-up | Dr Tegbir Vascular"
        description="Six defined steps from your first WhatsApp message to your final follow-up: reach out, team pre-screening, video consult with Dr Sidhu, scheduling & pre-arrival coordination, procedure & recovery, and follow-up on the same thread."
        keywords={["how to book Dr Sidhu", "vascular consultation India", "IR patient journey", "NRI video consult India", "Max Mohali IR appointment"]}
        url="/how-it-works"
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: "How it works — Dr Tegbir Sidhu's patient journey", description: "Six-step patient journey from WhatsApp introduction to final follow-up.", url: "/how-it-works" }),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }, { label: "How It Works", to: "/how-it-works" }]),
        howToJsonLd({
          name: "How to consult Dr Tegbir Sidhu for vascular interventional radiology",
          description: "The six-step patient journey — from first WhatsApp message to final follow-up.",
          url: "/how-it-works",
          steps: STEPS.map((s) => ({ name: s.t, text: s.d })),
        }),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <div className="kbd">— How it works</div>
          <h1 className="mt-8 font-serif text-6xl md:text-[8.5vw] leading-[0.96] tracking-tightest max-w-5xl">
            <MaskedReveal text={"From first"}/>
            <MaskedReveal text={"WhatsApp"} delay={0.1} className="italic font-normal"/>
            <MaskedReveal text={"to final follow-up."} delay={0.2}/>
          </h1>
          <p className="mt-10 max-w-2xl text-lg text-ink-muted leading-relaxed">
            Six defined steps. No handoffs between them. Every message, every review, every decision — the same doctor.
          </p>
        </FadeUp>

        <div className="mt-24 md:mt-32 relative">
          <div className="hidden md:block absolute left-[7.5rem] top-0 bottom-0 w-px bg-ink/10" aria-hidden />
          <ol className="space-y-16 md:space-y-24">
            {STEPS.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
                className="grid grid-cols-12 gap-6 md:gap-10 items-start"
              >
                <div className="col-span-3 md:col-span-2">
                  <div className="relative">
                    <div className="hidden md:block absolute -left-1 top-4 w-3 h-3 rounded-full bg-crimson" />
                    <div className="font-mono text-sm text-ink-muted">STEP</div>
                    <div className="font-serif text-6xl md:text-8xl leading-none tracking-tightest">{s.n}</div>
                  </div>
                </div>
                <div className="col-span-9 md:col-span-10 md:pl-8">
                  <h3 className="font-serif text-3xl md:text-5xl tracking-tightest leading-[1.05] max-w-3xl">{s.t}</h3>
                  <p className="mt-4 text-lg text-ink-muted leading-relaxed max-w-2xl">{s.d}</p>
                  <div className="mt-4 kbd">{s.meta}</div>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>

        <FadeUp className="mt-32">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-7">
              <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-tight">Two ways to start.</h3>
              <p className="mt-4 text-ink-muted max-w-xl">The form is best for detailed cases with imaging. WhatsApp is best for a quick first message.</p>
            </div>
            <div className="col-span-12 md:col-span-5 flex flex-wrap gap-3 md:justify-end items-end">
              <Link to="/contact" data-testid="hiw-cta-form" className="btn-pill-inv">Consultation form <ArrowUpRight size={14}/></Link>
              <a href={WHATSAPP_LINK()} target="_blank" rel="noreferrer" data-testid="hiw-cta-whatsapp" className="btn-pill">WhatsApp <ArrowUpRight size={14}/></a>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
